/**
 * useInvoiceFormLineItems
 *
 * Line-item calculation, totals/tax-breakdown memos, and the
 * add/remove/update handlers that mutate the line-items array.
 */

import { useCallback, useMemo } from 'react'

import { SupplyType as ProtoSupplyType } from '@/gen/proto'

import {
  createEmptyLineItem,
  type LineItem,
  type InvoiceTotals,
} from './invoice-form-helpers'
import { calculateGST } from '../../../utils/billing-helpers'
import { firstNonBlank, toSmallestUnit } from '../../../utils/formatters'

import type { InvoiceFormDerived } from './useInvoiceFormDerived'
import type { InvoiceFormState } from './useInvoiceFormState'

export interface InvoiceFormLineItemsParams {
  state: InvoiceFormState
  derived: InvoiceFormDerived
}

export interface InvoiceFormLineItems {
  calculateLineItem: (item: LineItem) => LineItem
  totals: InvoiceTotals
  taxBreakdown: Array<[number, { cgst: number; sgst: number; igst: number }]>
  addLineItem: () => void
  removeLineItem: (id: string) => void
  updateLineItem: (id: string, field: keyof LineItem, value: string) => void
  handleCustomerPOSelection: (poId: string) => void
}

export function useInvoiceFormLineItems(params: InvoiceFormLineItemsParams): InvoiceFormLineItems {
  const { state, derived } = params
  const calculateLineItem = useCalculateLineItem(state.invoiceType, derived.supplyType, state.currency)
  const totals = useTotals(state.lineItems, calculateLineItem, state.isRoundOffEnabled, state.currency)
  const taxBreakdown = useTaxBreakdown(state.lineItems, calculateLineItem)
  // URP purchases (unregistered supplier, reverse charge) carry no supplier tax,
  // so new lines default to 0%. The rate stays editable for manual RCM rates.
  const defaultGstRate = derived.isUrpPurchase ? '0' : '18'
  const handlers = useLineItemHandlers(state, calculateLineItem, defaultGstRate)

  return { calculateLineItem, totals, taxBreakdown, ...handlers }
}

// ─────────────────────────────────────────────────────────────────────────────
// calculateLineItem
// ─────────────────────────────────────────────────────────────────────────────

function toProtoSupplyType(supplyType: string): ProtoSupplyType {
  if (supplyType === 'intrastate') { return ProtoSupplyType.SUPPLY_TYPE_INTRASTATE }
  if (supplyType === 'export') { return ProtoSupplyType.SUPPLY_TYPE_EXPORT }
  return ProtoSupplyType.SUPPLY_TYPE_INTERSTATE
}

function useCalculateLineItem(
  invoiceType: string,
  supplyType: string,
  currency: string,
): (item: LineItem) => LineItem {
  return useCallback((item: LineItem): LineItem => {
    const quantity = parseFloat(item.quantity) || 0
    const unitPrice = parseFloat(item.unitPrice) || 0
    const discountPercent = parseFloat(item.discountPercent) || 0
    const gstRate = parseFloat(item.gstRate) || 0

    const safeCurrency = firstNonBlank(currency) ?? 'INR'
    const grossAmount = quantity * toSmallestUnit(unitPrice, safeCurrency)
    const discountAmount = (grossAmount * discountPercent) / 100
    const taxableAmount = grossAmount - discountAmount

    let cgstAmount = 0
    let sgstAmount = 0
    let igstAmount = 0
    if (invoiceType !== 'export') {
      const gst = calculateGST(taxableAmount, gstRate, toProtoSupplyType(supplyType))
      cgstAmount = gst.cgst
      sgstAmount = gst.sgst
      igstAmount = gst.igst
    }

    const totalTax = cgstAmount + sgstAmount + igstAmount
    const totalAmount = taxableAmount + totalTax
    return { ...item, taxableAmount, cgstAmount, sgstAmount, igstAmount, totalAmount }
  }, [invoiceType, supplyType, currency])
}

// ─────────────────────────────────────────────────────────────────────────────
// Totals
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_TOTALS_ACC = {
  subtotal: 0, discountAmount: 0, taxableAmount: 0,
  cgstAmount: 0, sgstAmount: 0, igstAmount: 0,
  cessAmount: 0, totalTax: 0, totalAmount: 0,
}

function accumulateTotals(items: LineItem[]): typeof EMPTY_TOTALS_ACC {
  return items.reduce((acc, item) => ({
    subtotal: acc.subtotal + item.taxableAmount,
    discountAmount: acc.discountAmount + 0,
    taxableAmount: acc.taxableAmount + item.taxableAmount,
    cgstAmount: acc.cgstAmount + item.cgstAmount,
    sgstAmount: acc.sgstAmount + item.sgstAmount,
    igstAmount: acc.igstAmount + item.igstAmount,
    cessAmount: 0,
    totalTax: acc.totalTax + item.cgstAmount + item.sgstAmount + item.igstAmount,
    totalAmount: acc.totalAmount + item.totalAmount,
  }), EMPTY_TOTALS_ACC)
}

function useTotals(
  lineItems: LineItem[],
  calculateLineItem: (item: LineItem) => LineItem,
  isRoundOffEnabled: boolean,
  currency: string,
): InvoiceTotals {
  return useMemo((): InvoiceTotals => {
    const calculatedItems = lineItems.map(calculateLineItem)
    const raw = accumulateTotals(calculatedItems)
    const factor = toSmallestUnit(1, currency)
    const roundOffAmount = isRoundOffEnabled
      ? Math.round(raw.totalAmount / factor) * factor - raw.totalAmount
      : 0
    return { ...raw, roundOffAmount, grandTotal: raw.totalAmount + roundOffAmount }
  }, [lineItems, calculateLineItem, isRoundOffEnabled, currency])
}

// ─────────────────────────────────────────────────────────────────────────────
// Tax breakdown
// ─────────────────────────────────────────────────────────────────────────────

function useTaxBreakdown(
  lineItems: LineItem[],
  calculateLineItem: (item: LineItem) => LineItem,
): Array<[number, { cgst: number; sgst: number; igst: number }]> {
  return useMemo(() => {
    const calculatedItems = lineItems.map(calculateLineItem)
    const byRate = new Map<number, { cgst: number; sgst: number; igst: number }>()
    calculatedItems.forEach((item) => {
      const rate = parseFloat(item.gstRate) || 0
      if (rate === 0) { return }
      const existing = byRate.get(rate) ?? { cgst: 0, sgst: 0, igst: 0 }
      existing.cgst += item.cgstAmount
      existing.sgst += item.sgstAmount
      existing.igst += item.igstAmount
      byRate.set(rate, existing)
    })
    return [...byRate.entries()]
      .filter(([, amounts]) => amounts.cgst > 0 || amounts.sgst > 0 || amounts.igst > 0)
      .sort(([a], [b]) => a - b)
  }, [lineItems, calculateLineItem])
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutating handlers
// ─────────────────────────────────────────────────────────────────────────────

interface LineItemHandlers {
  addLineItem: () => void
  removeLineItem: (id: string) => void
  updateLineItem: (id: string, field: keyof LineItem, value: string) => void
  handleCustomerPOSelection: (poId: string) => void
}

function useLineItemHandlers(
  state: InvoiceFormState,
  calculateLineItem: (item: LineItem) => LineItem,
  defaultGstRate: string,
): LineItemHandlers {
  const { setLineItems, customerPOs, setSelectedCustomerPOId, setPONumber } = state
  const addLineItem = useCallback(() => {
    setLineItems((items) => [...items, createEmptyLineItem(defaultGstRate)])
  }, [setLineItems, defaultGstRate])

  const removeLineItem = useCallback((id: string) => {
    setLineItems((items) => items.filter((item) => item.id !== id))
  }, [setLineItems])

  const updateLineItem = useCallback((id: string, field: keyof LineItem, value: string) => {
    setLineItems((items) =>
      items.map((item) => {
        if (item.id !== id) { return item }
        const updated = { ...item, [field]: value }
        return calculateLineItem(updated)
      }),
    )
  }, [calculateLineItem, setLineItems])

  const handleCustomerPOSelection = useCallback((poId: string) => {
    setSelectedCustomerPOId(poId)
    const selectedPO = customerPOs.find((po) => po.id === poId)
    if (selectedPO) { setPONumber(selectedPO.customerPoNumber) }
  }, [customerPOs, setSelectedCustomerPOId, setPONumber])

  return { addLineItem, removeLineItem, updateLineItem, handleCustomerPOSelection }
}
